'use client';

import { useEffect, useRef } from 'react';

interface Node {
  x: number;
  y: number;
  z: number; // depth: 0.3 (far) to 1.6 (near)
  vx: number;
  vy: number;
  vz: number;
  baseRadius: number;
}

const NODE_COUNT = 130;
const MAX_NODES = 190;
const CONNECTION_DIST = 220;
const MOUSE_RADIUS = 240;
const MOUSE_FORCE = 0.45;
const DEPTH_SCALE = 600;
const ENTROPY = 0.01; // random perturbation strength
const DAMPING = 0.998;

export function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const nodesRef = useRef<Node[]>([]);
  const lastHeightRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function getThemeColors() {
      const style = getComputedStyle(document.documentElement);
      const brand = style.getPropertyValue('--brand-glow').trim() || '330 81% 60%';
      const background = style.getPropertyValue('--background').trim() || '228 12% 98%';
      const isDark = document.documentElement.classList.contains('dark');
      return { brand, background, isDark };
    }

    function getDocHeight() {
      return document.documentElement.scrollHeight;
    }

    function getDocWidth() {
      return window.innerWidth;
    }

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const w = getDocWidth();
      const h = getDocHeight();
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      canvas!.style.width = w + 'px';
      canvas!.style.height = h + 'px';
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function createNode(xRange: number, yRange: number, xOffset = 0, yOffset = 0): Node {
      return {
        x: xOffset + Math.random() * xRange,
        y: yOffset + Math.random() * yRange,
        z: 0.4 + Math.random() * 1.0,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        vz: (Math.random() - 0.5) * 0.008,
        baseRadius: Math.random() * 1.6 + 1.2,
      };
    }

    function initNodes() {
      const w = getDocWidth();
      const h = getDocHeight();
      lastHeightRef.current = h;
      nodesRef.current = Array.from({ length: NODE_COUNT }, () => createNode(w, h));
    }

    function addNodesInRegion(xRange: number, yRange: number, xOffset: number, yOffset: number, count: number) {
      for (let i = 0; i < count; i++) {
        if (nodesRef.current.length >= MAX_NODES) break;
        nodesRef.current.push(createNode(xRange, yRange, xOffset, yOffset));
      }
    }

    function draw() {
      const w = getDocWidth();
      const h = getDocHeight();
      const { brand, background, isDark } = getThemeColors();

      // Fill background so particles share the same plane as the page background
      ctx!.fillStyle = `hsl(${background.replace(/ /g, ',')})`;
      ctx!.fillRect(0, 0, w, h);

      const nodes = nodesRef.current;
      const mouse = mouseRef.current;

      // Update positions with entropy (wrap-around, no bouncing back)
      for (const node of nodes) {
        node.x += node.vx * node.z;
        node.y += node.vy * node.z;
        node.z += node.vz;

        // Wrap around edges (entropy: never bounce back, continuous flow)
        if (node.x < -60) node.x = w + 60;
        if (node.x > w + 60) node.x = -60;
        if (node.y < -60) node.y = h + 60;
        if (node.y > h + 60) node.y = -60;
        if (node.z < 0.25) { node.z = 0.25; node.vz *= -1; }
        if (node.z > 1.55) { node.z = 1.55; node.vz *= -1; }

        // Entropy injection: random perturbations create increasing disorder
        node.vx += (Math.random() - 0.5) * ENTROPY;
        node.vy += (Math.random() - 0.5) * ENTROPY;
        node.vz += (Math.random() - 0.5) * ENTROPY * 0.3;

        // Gentle damping to prevent runaway speeds
        node.vx *= DAMPING;
        node.vy *= DAMPING;
        node.vz *= DAMPING;

        // Mouse interaction: repel in 2D, pull toward camera in Z
        const dx = node.x - mouse.x;
        const dy = node.y - mouse.y;
        const dist2D = Math.sqrt(dx * dx + dy * dy);
        if (dist2D < MOUSE_RADIUS && dist2D > 0) {
          const force = (MOUSE_RADIUS - dist2D) / MOUSE_RADIUS;
          node.vx += (dx / dist2D) * force * MOUSE_FORCE;
          node.vy += (dy / dist2D) * force * MOUSE_FORCE;
          node.vz += force * 0.015; // mouse pulls particles closer
        }
      }

      // Draw connections (depth-aware + mouse focus)
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i]!;
          const b = nodes[j]!;
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dz = (a.z - b.z) * DEPTH_SCALE;
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < CONNECTION_DIST) {
            const avgZ = (a.z + b.z) / 2;
            const mx = (a.x + b.x) / 2;
            const my = (a.y + b.y) / 2;
            const mdx = mx - mouse.x;
            const mdy = my - mouse.y;
            const mouseDist = Math.sqrt(mdx * mdx + mdy * mdy);
            const focusFactor = mouseDist < MOUSE_RADIUS
              ? 1.3 + (1 - mouseDist / MOUSE_RADIUS) * 0.5
              : Math.max(0.35, 1 - mouseDist / (MOUSE_RADIUS * 3));

            const alpha = (1 - dist / CONNECTION_DIST) * avgZ * (isDark ? 0.6 : 0.35) * focusFactor;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.strokeStyle = isDark
              ? `hsla(${brand.split(' ')[0]}, 75%, 72%, ${alpha})`
              : `hsla(${brand.split(' ')[0]}, 65%, 52%, ${alpha})`;
            ctx!.lineWidth = avgZ * 0.9;
            ctx!.stroke();
          }
        }
      }

      // Draw nodes (depth-of-field + mouse focus)
      for (const node of nodes) {
        const hue = brand.split(' ')[0];
        const r = node.baseRadius * node.z;

        // Mouse focus: particles near mouse are larger and brighter
        const mdx = node.x - mouse.x;
        const mdy = node.y - mouse.y;
        const mouseDist = Math.sqrt(mdx * mdx + mdy * mdy);
        const focusFactor = mouseDist < MOUSE_RADIUS
          ? 1.4 + (1 - mouseDist / MOUSE_RADIUS) * 0.6
          : Math.max(0.4, 1 - mouseDist / (MOUSE_RADIUS * 3));

        // Depth-of-field: far particles are fainter
        const depthAlpha = Math.max(0.25, node.z / 1.6);

        // Outer glow (atmospheric)
        ctx!.beginPath();
        ctx!.arc(node.x, node.y, r * 5, 0, Math.PI * 2);
        ctx!.fillStyle = isDark
          ? `hsla(${hue}, 80%, 72%, ${0.16 * depthAlpha * focusFactor})`
          : `hsla(${hue}, 70%, 58%, ${0.14 * depthAlpha * focusFactor})`;
        ctx!.fill();

        // Mid glow
        ctx!.beginPath();
        ctx!.arc(node.x, node.y, r * 2.5, 0, Math.PI * 2);
        ctx!.fillStyle = isDark
          ? `hsla(${hue}, 85%, 78%, ${0.32 * depthAlpha * focusFactor})`
          : `hsla(${hue}, 75%, 58%, ${0.26 * depthAlpha * focusFactor})`;
        ctx!.fill();

        // Core
        ctx!.beginPath();
        ctx!.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx!.fillStyle = isDark
          ? `hsla(${hue}, 95%, 85%, ${0.98 * depthAlpha * focusFactor})`
          : `hsla(${hue}, 85%, 58%, ${0.92 * depthAlpha * focusFactor})`;
        ctx!.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    }

    function onMouseMove(e: MouseEvent) {
      // Use viewport-relative coordinates because the canvas is position:fixed,
      // so only particles within the visible viewport (0~innerHeight) are seen.
      mouseRef.current = { x: e.clientX, y: e.clientY };
    }

    function onMouseLeave() {
      mouseRef.current = { x: -1000, y: -1000 };
    }

    function onScrollOrResize() {
      const newH = getDocHeight();
      const newW = getDocWidth();
      const prevH = lastHeightRef.current;

      if (Math.abs(prevH - newH) > 50 || Math.abs(canvas!.width / (window.devicePixelRatio || 1) - newW) > 50) {
        resize();

        if (newH > prevH + 100) {
          const extraArea = newW * (newH - prevH);
          const totalArea = newW * newH;
          const extraCount = Math.max(3, Math.floor(NODE_COUNT * (extraArea / totalArea)));
          addNodesInRegion(newW, newH - prevH, 0, prevH, extraCount);
        }

        lastHeightRef.current = newH;
      }
    }

    function handleResize() {
      resize();
      initNodes();
    }

    resize();
    initNodes();
    draw();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', onScrollOrResize);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);

    const observer = new MutationObserver(() => {});
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 z-0 pointer-events-none"
      aria-hidden="true"
    />
  );
}
